import React, { Dispatch, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { SettlementFilters } from '@/apis/settlement/interface';

import Card from '@/components/Card';
import TextComponent from '@/components/TextComponent';
import CustomDatePicker from '@/components/CustomDatePicker';
import ButtonComponent from '@/components/ButtonComponent';
import SelectComponent from '@/components/SelectComponent';
import Tooltip from '@/components/Tooltip';
import { submitEnterKey } from '@/utils/form';
import Label from '@/components/SearchFilter/Label';

import { searchTypeList } from '@/assets/data/selectOptions';
import { ReactComponent as CalendarIcon } from '@/assets/images/common/calendar.svg';
import { ReactComponent as RadioSelected } from '@/assets/images/common/radioSelected.svg';
import { ReactComponent as RadioUnselected } from '@/assets/images/common/radioUnselected.svg';
import { ReactComponent as Question } from '@/assets/images/layout/question.svg';
import theme from '@/styles/theme';

type TermType = 'day' | 'month';
type SearchType = 'all' | 'username' | 'phone';

interface Props {
  filter: SettlementFilters;
  setFilter: Dispatch<SettlementFilters>;
}

const SearchFilter = (props: Props) => {
  const { filter, setFilter } = props;

  const [termType, setTermType] = useState<TermType>('day');
  const [startDate, setStartDate] = useState<Date | null>(
    moment(filter.from).toDate()
  );
  const [endDate, setEndDate] = useState<Date | null>(
    moment(filter.to).toDate()
  );

  const [searchType, setSearchType] = useState<SearchType>('all');
  const [searchInput, setSearchInput] = useState<string>('');

  const onChangeFilterType = (type: TermType) => {
    type === 'month'
      ? setStartDate(moment().startOf('month').toDate())
      : setStartDate(moment().subtract(1, 'M').toDate());

    setTermType(type);
    setEndDate(moment().toDate());
  };

  const onChangeSearchType = (type: SearchType) => {
    type === 'all' && setSearchInput('');
    setSearchType(type);
  };

  const onChangeEndDate = (date: Date | null) => {
    setEndDate(date);
    if (moment(date).diff(startDate, 'days') < 0) {
      setStartDate(moment(date).subtract(1, 'M').toDate());
    }
  };

  const onSearchByFilter = () => {
    const searchTerm =
      termType === 'month'
        ? {
            from: moment(startDate).format('YYYY-MM-DD'),
            to: moment(startDate).endOf('month').format('YYYY-MM-DD')
          }
        : {
            from: moment(startDate).format('YYYY-MM-DD'),
            to: moment(endDate).format('YYYY-MM-DD')
          };

    const searchContent = { phone: '', username: '' };
    if (searchType !== 'all') {
      searchContent[searchType] = searchInput.trim();
    }

    setFilter({
      ...filter,
      ...searchContent,
      ...searchTerm
    });
  };

  return (
    <Card>
      <CardBody>
        <FilterItem>
          <Row>
            <Label>검색 기간</Label>
            <a data-tip data-for="threeMonth">
              <Question width={18} height={18} />
            </a>
            <Tooltip id="threeMonth" place="top" type="info" effect="solid">
              <TextComponent color={'#fff'}>
                정산 조회 기간은 최대 1개월입니다.
              </TextComponent>
            </Tooltip>
          </Row>
          <RadioWrapper>
            <RadioItem onClick={() => onChangeFilterType('day')}>
              {termType === 'day' ? <RadioSelected /> : <RadioUnselected />}{' '}
              <Label>일별로 검색</Label>
            </RadioItem>
            <RadioItem onClick={() => onChangeFilterType('month')}>
              {termType === 'month' ? <RadioSelected /> : <RadioUnselected />}
              <Label>월별로 검색</Label>
            </RadioItem>
          </RadioWrapper>
          <InputWrapper>
            {termType === 'day' ? (
              <>
                <CustomDatePicker
                  selected={startDate}
                  onChange={date => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  minDate={moment(endDate).subtract(1, 'M').toDate()}
                  maxDate={moment().toDate()}
                  dateFormat={'yyyy.MM.dd'}
                  customInput={<DateInput />}
                />
                <Dash>-</Dash>
                <CustomDatePicker
                  selected={endDate}
                  onChange={date => onChangeEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat={'yyyy.MM.dd'}
                  maxDate={moment().toDate()}
                  customInput={<DateInput />}
                />
              </>
            ) : (
              <>
                <CustomDatePicker
                  selected={startDate}
                  onChange={date => setStartDate(date)}
                  dateFormat={'yyyy.MM'}
                  maxDate={new Date()}
                  showMonthYearPicker
                  showFullMonthYearPicker
                  showTwoColumnMonthYearPicker
                  customInput={<DateInput width={200} />}
                />
                <Calendar />
              </>
            )}
          </InputWrapper>
        </FilterItem>
        <FilterItem>
          <Label>검색 조건</Label>
          <SearchRow>
            <SearchInput>
              <SelectComponent
                options={searchTypeList}
                defaultValue={searchTypeList[0]}
                onChange={e => onChangeSearchType(e)}
                selectStyle={{
                  width: '120px'
                }}
                singleValue={{
                  color: '#000'
                }}
              />
              <TextInput
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                disabled={searchType === 'all'}
                onKeyDown={e => submitEnterKey(e, onSearchByFilter)}
              />
            </SearchInput>
            <ButtonComponent
              bgColor={theme.DF_COLOR.WHITE}
              borderColor={theme.DF_COLOR.GREEN500}
              textColor={theme.DF_COLOR.GREEN500}
              fontWeight={700}
              fontSize={18}
              text={'조회'}
              onClick={onSearchByFilter}
              style={{ padding: '0 20px' }}
            />
          </SearchRow>
        </FilterItem>
      </CardBody>
    </Card>
  );
};

export default SearchFilter;

const CardBody = styled.div`
  padding: 25px 20px;
  width: 470px;
`;

const FilterItem = styled.div``;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 22px;
`;

const DateInput = styled.input<{ width?: number }>`
  border: 1px solid ${theme.DF_COLOR.GRAY100};
  padding: 11px 15px;
  border-radius: 5px;
  max-width: ${({ width }) => (width ? width : 140)}px;
  font-size: 16px;
`;

const Dash = styled(TextComponent)`
  margin: 0 16px;
`;

const Calendar = styled(CalendarIcon)`
  margin-left: 8px;
`;

const RadioWrapper = styled.div`
  display: flex;
  margin: 20px 0 16px;
`;

const RadioItem = styled.div`
  display: flex;
  margin-right: 24px;
  align-items: center;
  cursor: pointer;

  > * {
    &:nth-child(1) {
      margin-right: 5px;
    }
  }
`;

const Row = styled.div`
  display: flex;
`;
const SearchRow = styled(Row)`
  margin-top: 20px;
`;

const SearchInput = styled.div`
  display: flex;
  flex: 8;
`;

const TextInput = styled.input`
  margin: 0 8px;
  border: 1px solid ${theme.DF_COLOR.GRAY100};
  width: 100%;
  border-radius: 5px;
  font-size: 16px;
  padding: 0 15px;

  &:disabled {
    opacity: 0.5;
  }
`;
